import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Calendar as CalendarIcon, Download, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export default function CalendarSyncSection() {
  const { t } = useTranslation("calendar");
  const { user } = useAuth();
  const [icalToken, setIcalToken] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("ical_token")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        setIcalToken(data?.ical_token ?? null);
      });
  }, [user]);

  const feedUrl = icalToken
    ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ical-feed?token=${icalToken}`
    : null;

  const webcalUrl = feedUrl?.replace(/^https?:\/\//, "webcal://") ?? null;

  const handleDownloadIcs = () => {
    if (!feedUrl) return;
    window.open(feedUrl, "_blank");
  };

  const handleResetToken = async () => {
    if (!user) return;
    const newToken = crypto.randomUUID().replace(/-/g, "").slice(0, 32);
    const { error } = await supabase
      .from("profiles")
      .update({ ical_token: newToken })
      .eq("id", user.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setIcalToken(newToken);
    toast.success(t("resetSuccess"));
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm">{t("calendarSync")}</h3>
        </div>
        <p className="text-xs text-muted-foreground">{t("syncDescription")}</p>

        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-xs"
            disabled={!webcalUrl}
            onClick={() => webcalUrl && window.open(webcalUrl)}
          >
            <CalendarIcon className="h-3.5 w-3.5 mr-2" />
            {t("googleCalendar")}
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-xs"
            disabled={!webcalUrl}
            onClick={() => webcalUrl && window.open(webcalUrl)}
          >
            <CalendarIcon className="h-3.5 w-3.5 mr-2" />
            {t("appleCalendar")}
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-xs"
            disabled={!feedUrl}
            onClick={handleDownloadIcs}
          >
            <Download className="h-3.5 w-3.5 mr-2" />
            {t("downloadIcs")}
          </Button>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground"
            >
              <RotateCcw className="h-3 w-3 mr-1.5" />
              {t("resetLink")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("resetWarning")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("resetWarningDescription")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={handleResetToken}>
                {t("resetConfirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
